<script setup>
import {ref, onBeforeUnmount, watch} from "vue";
import {createStream} from "@/services/stream.service";
import {createCSV, download} from "@/helpers/file";

const showModal = ref(false);
const question = ref("");
const loading = ref(false);
const stream = ref(null);
const rounds = ref([]);
const responseId = ref('');
const systemMessages = ref([]);
const logMessage = ref('');
const loaderMessage = ref('');
const content = ref(null);

function downloadFile(file) {
    let columns = file.columns.reduce((acc, column) => {
        acc[column.column] = column.label;
        return acc;
    }, {});

    let data = file.data.map(item =>
        file.columns.reduce((row, column) => {
            row[column.column] = item[column.column];
            return row;
        }, {})
    );

    download(createCSV(data, columns), 'response.csv');
}

function sanitizeAnswer(answer) {
    return answer
        .replace(/\\r\\n|\\n|\\r/g, '<br>')
        .replace(/\r\n|\n|\r/g, '<br>');
}

function clearStream() {
    if (stream.value) {
        stream.value.close();
        stream.value = null;
    }
}

function sendRequest() {
    if (!question.value.trim()) {
        return;
    }

    // close previous stream if still open to avoid multiple concurrent connections
    clearStream();

    const params = new URLSearchParams();

    params.set('question', btoa(unescape(encodeURIComponent(question.value))));

    if (responseId.value?.length) {
        params.set('response_id', btoa(unescape(encodeURIComponent(responseId.value))));
    }

    stream.value = createStream('/chat-stream/get-ai-response?' + params.toString());

    let round = {
        question: question.value,
        answer: '',
        output: null
    };

    rounds.value.push(round);
    question.value = '';
    loading.value = true;

    stream.value.onerror = (err) => {
        round.answer = 'SSE connection error. Please try again later.';
        console.error('SSE error', err);
        clearStream();

        // stop loading if connection fails
        loading.value = false;
    };

    stream.value.onmessage = function(event) {
        loaderMessage.value = '';
        round.answer += event.data;
    };

    stream.value.addEventListener('system', function(event) {
        systemMessages.value.push(event.data);
    });

    stream.value.addEventListener('log', function(event) {
        logMessage.value += event.data;
    });

    stream.value.addEventListener('loader', function(event) {
        loaderMessage.value = event.data;
    });

    stream.value.addEventListener('done', function(event) {
        const data = JSON.parse(event.data);
        loaderMessage.value = '';

        round.output = data.output;
        round.answer = data.answer;
        responseId.value = data.response_id;

        loading.value = false;

        stream.value.close();
        stream.value = null;
    });

    requestAnimationFrame(() => {
        content.value.scrollTop = content.value.scrollHeight;
    });
}

// ensure we always close stream when modal is hidden or component unmounts
watch(showModal, (value) => {
    if (!value && stream.value) {
        try { stream.value.close(); } catch (e) {
            /* ignore */
        }
        stream.value = null;
    }
});

onBeforeUnmount(() => {
    if (stream.value) {
        try { stream.value.close(); } catch (e) {
            /* ignore */
        }
        stream.value = null;
    }
});

function open(predefinedQuestion) {
    rounds.value = [];
    responseId.value = '';
    loading.value = false;
    question.value = predefinedQuestion;
    systemMessages.value = [];
    logMessage.value = '';

    showModal.value = true;
    sendRequest();
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" hide-footer hide-header modal-class="modal-ai-chat">
        <!-- <a href="#" class="close">Back</a> -->
        <section class="section-ai-chat">
            <button @click="showModal = false" type="button" aria-label="Close" class="close"></button>

            <div class="ai-chat__inner">
                <div class="ai-chat__content" ref="content">
                    <!-- <h5 class="heading5 heading5--secondary">Find VCs, accelerators, universities, incubators and more.</h5> -->
                    
                    <div class="ai-chat">
                        <div
                            v-for="(round, index) in rounds"
                            :key="index"
                            class="ai-chat__inner"
                        >
                            <div class="ai-chat__user">
                                {{round.question}}
                            </div>

                            <div class="ai-chat__machine" :class="{'long-div': index === rounds.length - 1}">
                                <div v-if="loaderMessage.length && index === rounds.length -1">
                                    <div class="ai-loader">
                                        <span>{{loaderMessage}}</span>
                                        <span class="ai-loader__circle ml-2"></span>
                                        <span class="ai-loader__circle"></span>
                                        <span class="ai-loader__circle"></span>
                                    </div>
                                </div>

                                <p v-html="sanitizeAnswer(round.answer)"/>

                                <table class="ai-chat__table" v-if="round.output?.table">
                                    <thead>
                                    <tr>
                                        <th v-for="column in round.output.table.columns" :key="column.column">
                                            {{ column.label }}
                                        </th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-for="(row, rowIndex) in round.output.table.data" :key="rowIndex">
                                            <td v-for="column in round.output.table.columns" :key="column.column">
                                                <a
                                                    v-if="round.output.table.company_data_fields && round.output.table.company_data_fields.name === column.column"
                                                    :href="`/startups/${row[round.output.table.company_data_fields.id]}`"
                                                    target="_blank"
                                                >
                                                    {{ row[round.output.table.company_data_fields.name] }}
                                                </a>

                                                <template v-else>
                                                    {{ row[column.column] }}
                                                </template>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <button
                                    v-if="round.output?.file"
                                    class="button-csv"
                                    @click="downloadFile(round.output.file)"
                                >
                                    Download CSV
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="ai-chat__footer">
                        <div v-if="!loading" class="card--ai__search">
                            <input
                                v-focus
                                v-model="question"
                                placeholder="Ask about startups or partnerships"
                                class="card--ai__search__input"
                                @keyup.enter="sendRequest"
                            >

                            <button class="card--ai__search__button" @click="sendRequest"></button>
                        </div>

                        <div style="margin-top: 40px;
                                    align-items: flex-start;
                                    position: absolute;
                                    left: 40px;
                                    bottom: 40px;">
                            <div>
                                <b-button v-b-toggle.messages variant="primary">System Messages ({{systemMessages.length}})</b-button>
                                <b-collapse id="messages" class="mt-2">
                                    <div v-for="(message, index) in systemMessages" :key="index">
                                        {{ message }}
                                    </div>

                                    <a class="link" @click="systemMessages = []">Clear</a>
                                </b-collapse>
                            </div>

                            <b-button v-b-toggle.logs variant="primary">Answer Log</b-button>
                            <b-collapse id="logs" class="mt-2">
                                {{logMessage}}

                                <br>
                                <a class="link" @click="logMessage = ''">Clear</a>
                            </b-collapse>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </b-modal>
</template>

<style scoped>
    .long-div {
        min-height: 600px;
    }

    .ai-chat__content {
        overflow-y: auto;   /* REQUIRED */
        min-height: 0;
    }

    .ai-chat__inner {
        height: 300vh;
        min-height: 0;
    }
</style>
